import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { Check, Loader2, RefreshCw, LogOut, Copy, CheckCheck } from 'lucide-react'

interface ProvisioningStepProps {
  status: 'idle' | 'loading' | 'success' | 'error'
  errorMessage: string
  isAuthError?: boolean
  assistantName?: string
  emailAddress?: string
  onRetry: () => void
  onSignOut?: () => void
}

export default function ProvisioningStep({
  status,
  errorMessage,
  isAuthError,
  assistantName,
  emailAddress,
  onRetry,
  onSignOut,
}: ProvisioningStepProps) {
  const { t } = useTranslation('onboarding')
  const { t: tc } = useTranslation('common')
  const [copied, setCopied] = useState(false)

  const displayName = assistantName || 'TendBot'

  const handleCopyEmail = async () => {
    if (!emailAddress) return
    try {
      await navigator.clipboard.writeText(emailAddress)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch { /* clipboard not available */ }
  }

  return (
    <div className="text-center">
      <div className="mb-6">
        {status === 'loading' && (
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
          >
            <Loader2 size={48} className="text-gold animate-spin mx-auto" />
          </motion.div>
        )}
        {status === 'success' && (
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            className="w-16 h-16 rounded-full bg-emerald/20 flex items-center justify-center mx-auto"
          >
            <Check size={32} className="text-emerald" />
          </motion.div>
        )}
        {status === 'error' && (
          <div className="w-16 h-16 rounded-full bg-rose/20 flex items-center justify-center mx-auto">
            <span className="text-rose text-2xl">!</span>
          </div>
        )}
      </div>

      <h2 className="font-display text-2xl text-ivory font-semibold mb-2">
        {status === 'success'
          ? t('provisioning.isReady', { name: displayName })
          : status === 'error'
            ? t('provisioning.somethingWrong')
            : t('provisioning.settingUp', { name: displayName })}
      </h2>

      {status === 'loading' && (
        <p className="text-pearl/60 text-sm">{t('provisioning.creating')}</p>
      )}

      {status === 'success' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="space-y-3"
        >
          {emailAddress && (
            <div className="flex items-center justify-center gap-2">
              <p className="text-pearl/50 text-sm">
                {t('provisioning.delegationInbox')}
                {' '}
                <span className="text-gold font-medium">{emailAddress}</span>
              </p>
              <button
                onClick={handleCopyEmail}
                className="text-pearl/55 hover:text-pearl/80 transition-colors"
                aria-label={t('provisioning.copyEmail')}
              >
                {copied ? <CheckCheck size={14} className="text-emerald" /> : <Copy size={14} />}
              </button>
            </div>
          )}
          <p className="text-pearl/60 text-sm">{t('provisioning.redirecting')}</p>
        </motion.div>
      )}

      {status === 'error' && (
        <div>
          <p className="text-rose/80 text-sm mb-4">{errorMessage}</p>
          {isAuthError && onSignOut ? (
            <button
              onClick={onSignOut}
              className="flex items-center gap-2 mx-auto bg-gold hover:bg-gold-light text-midnight font-semibold px-6 py-2.5 rounded-xl transition-colors"
            >
              <LogOut size={16} />
              {tc('actions.backToLogin', 'Back to login')}
            </button>
          ) : (
            <button
              onClick={onRetry}
              className="flex items-center gap-2 mx-auto bg-gold hover:bg-gold-light text-midnight font-semibold px-6 py-2.5 rounded-xl transition-colors"
            >
              <RefreshCw size={16} />
              {tc('actions.retry')}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
